import ProviderFilter from 'components/ProviderFilter'
import { ALL_GAME_TAG } from 'config/constants/gameTags'
import { useAnalytics } from 'hooks/useAnalytics'
import useDebounceCallback from 'hooks/useDebounceCallback'
import { useIsomorphicEffect } from 'hooks/useIsomorphicEffect'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import GameService from 'services/GameService'
import { useAppDispatch, useAppSelector } from 'state'
import { addSearchHistoryText } from 'state/session/actions'
import styled from 'styled-components'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Grid from 'UIKit/Box/Grid'
import EmptyContent from 'UIKit/EmptyContent'
import HunnyLazyList from 'UIKit/HunnyLazyList'
import OpenEffect from 'UIKit/OpenEffect'
import TransText from 'UIKit/Text/TransText'
import { transformHunnyRequest } from 'utils/requestHelper'
import { animate } from 'utils/scroll'
import GameItem from 'views/Games/components/GameItem'
import GameItemLoader from 'views/Games/components/GameItemLoader'
import GamesFilter from 'views/Games/components/GamesFilter'
import useGamesFilter from 'views/Games/components/GamesFilter/useGamesFilter'
import { mapSortType } from 'views/Games/components/GamesFilter/utils'
import GameTagGroup from 'views/Games/components/GameTagGroup'

import { GamesSearchFilterOptions } from 'views/Games/components/GamesFilter/config'
import { RowBetween, RowMiddle } from '../Row'

const LazyGameItem = ({ item }) => <GameItem game={item} p="0px !important" />

type SearchResultProps = { searchText?: string }

const SearchResult: React.FC<SearchResultProps> = ({ searchText }) => {
  const debounceCallback = useDebounceCallback()
  const dispatch = useAppDispatch()

  const defaultScreenTags = useAppSelector((state) => state.app.defaultScreenTags)
  const tags = useMemo(() => defaultScreenTags.map((screenTag) => screenTag.tag), [defaultScreenTags])

  const { recordEvent } = useAnalytics()
  const [selectedTag, setSelectedTag] = useState(ALL_GAME_TAG)
  const [filterParams, setFilterParams] = useState({ searchText, selectedTag })
  const [providers, setProviders] = useState<string[]>([])

  const { selectedFilterType, onSelectOption } = useGamesFilter(GamesSearchFilterOptions[0])
  const { gameSortType, gameSortOrder } = mapSortType(selectedFilterType)

  const scrollRef = useRef<HTMLDivElement>(null)

  const request = useCallback(
    (offset: number, limit: number) => {
      if (offset === 0) {
        recordEvent('search', filterParams.searchText)
      }

      return transformHunnyRequest(
        GameService.getGames(
          {
            providerCodes: providers,
            tags: [filterParams.selectedTag],
            query: filterParams.searchText,
            gameSortType,
            gameSortOrder,
          },
          offset,
          limit,
        ),
        (response) => {
          if (response?.data?.items?.length > 0) {
            debounceCallback(() => dispatch(addSearchHistoryText({ text: filterParams.searchText })), 1000)
          }

          return response
        },
      )
    },
    [filterParams, providers, gameSortType, gameSortOrder],
  )

  useIsomorphicEffect(() => {
    const scrollElement = scrollRef.current
    if (scrollElement.scrollTop === 0) {
      setFilterParams({ searchText, selectedTag })
    } else {
      animate('scrollTop', scrollElement, 0, 200, () => {
        setFilterParams({ searchText, selectedTag })
      })
    }
  }, [searchText, selectedTag])

  return (
    <OpenEffect openType="grow" mt={['12px', '', '24px']} height="100%" overflow="hidden" position="relative">
      <Flex
        flexDirection={['column', 'column', 'column', 'column']}
        justifyContent="space-between"
        pr={['12px', '', '0px']}
        mb="4px"
        width="100%"
      >
        <StyledGameTag
          value={selectedTag}
          onTagChanged={setSelectedTag}
          tags={tags}
          backgroundBlurColor={colors.background}
          px={['12px', '', '0px']}
        />

        <RowBetween
          mt={['32px', '', '', '20px']}
          pl={['12px', '', '', '0px']}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <TransText fontSize="16px" fontWeight={700} color="#6C727E" display={['block', '', '', 'block']} mr="4px">
            Games
          </TransText>
          <RowMiddle height="36px">
            <StyledGameFilter
              selectedOption={selectedFilterType}
              onSelectOption={onSelectOption}
              mr="8px"
              height="100%"
              filterOptions={GamesSearchFilterOptions}
            />
            <ProviderFilter selectedProviders={providers} onProvidersChanged={setProviders} height="100%" />
          </RowMiddle>
        </RowBetween>
      </Flex>
      <Box
        ref={scrollRef}
        height={['calc(var(--screen-height) - 285px)', '', 'calc(75vh - 166px)']}
        overflow="auto"
        mt={['12px', '', '0px']}
        pt={['0px', '', '12px']}
        pl={['12px', '', '0px']}
        pr={['12px', '', '16px']}
      >
        <Grid
          position="relative"
          gridGap={['12px', '', '16px']}
          gridTemplateColumns={[
            '1fr 1fr',
            '1fr 1fr',
            '1fr 1fr 1fr',
            '1fr 1fr 1fr',
            '1fr 1fr 1fr 1fr',
            '1fr 1fr 1fr 1fr 1fr',
          ]}
        >
          <HunnyLazyList
            uniqueKey="code"
            Item={LazyGameItem}
            noContent={
              <Box position="absolute" top="64px" width="100%" left="0px">
                <EmptyContent justifyContent="center" width="100%" content="We couldn't find any games" />
              </Box>
            }
            Loader={
              <>
                <GameItemLoader p="0px !important" />
                <GameItemLoader p="0px !important" />
                <GameItemLoader p="0px !important" />
                <GameItemLoader p="0px !important" />
                <GameItemLoader p="0px !important" />
              </>
            }
            request={request}
            limit={20}
          />
        </Grid>
      </Box>
    </OpenEffect>
  )
}

const StyledGameFilter = styled(GamesFilter)`
  padding-right: 0px !important;
`

const StyledGameTag = styled(GameTagGroup)`
  padding-right: 0px !important;
  max-width: calc(100% - 60px - 12px);

  .tabs-left-blur,
  .tabs-right-blur {
    width: 100px !important;
  }

  & .game-tag-item {
    min-width: fit-content;
    padding: 4px 12px;
    ${({ theme }) => theme.mediaQueries.sm} {
      min-width: 110px;
    }
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    max-width: calc(100% - 125px - 24px);

    .tabs-left-blur,
    .tabs-right-blur {
      width: 150px !important;
    }
  }
`

export default SearchResult
